import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Alert,
  AlertIcon,
  Button,
  Box,
  Text,
  VStack,
} from "@chakra-ui/react";
import { registerBadNumberDialog } from "../../utils/badNumbers";

// The pop-up behind confirmBadNumbers() (utils/badNumbers.js). Mounted once
// in App.js; any manual send can then await the user's answer:
// "send" | "skip" | "cancel".
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

export default function BadNumberConfirmHost() {
  const [request, setRequest] = useState(null); // { hits, total, batch, resolve }
  const cancelRef = useRef();

  useEffect(
    () =>
      registerBadNumberDialog(
        ({ hits, total, batch }) =>
          new Promise((resolve) => setRequest({ hits, total, batch, resolve })),
      ),
    [],
  );

  const answer = (choice) => {
    request?.resolve(choice);
    setRequest(null);
  };

  const hits = request?.hits || [];
  const single = hits.length === 1;
  // A batch only warns and skips: no Twilio spend on texts that cannot arrive.
  const batch = Boolean(request?.batch);
  const sendable = request ? request.total - hits.length : 0;

  return (
    <AlertDialog
      isOpen={Boolean(request)}
      leastDestructiveRef={cancelRef}
      onClose={() => answer("cancel")}
      size="lg"
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {batch
              ? `${hits.length} bad number${single ? "" : "s"} will be skipped`
              : "This number is known to be bad"}
          </AlertDialogHeader>
          <AlertDialogBody>
            {batch ? (
              <Alert status="warning" borderRadius="md" mb={3}>
                <AlertIcon />
                <Text>
                  Earlier texts to {single ? "this number" : "these numbers"} failed with a
                  permanent error, so {single ? "it" : "they"} will not be texted — no Twilio
                  credits spent on texts that cannot arrive. Correct the number on file to text{" "}
                  {single ? "it" : "them"} again.
                  {sendable > 0 && ` The other ${sendable} will be sent as usual.`}
                </Text>
              </Alert>
            ) : (
              <Text mb={3}>
                Earlier texts to this number failed with a permanent error, so this text will
                most likely not be delivered. Correct the number on file instead, if you can.
              </Text>
            )}
            <VStack align="stretch" spacing={2} maxH="240px" overflowY="auto">
              {hits.map((h, i) => (
                <Box key={`${h.phone}-${i}`} borderWidth="1px" borderRadius="md" p={2}>
                  <Text fontWeight="semibold">
                    {h.name ? `${h.name} · ` : ""}
                    {h.phone}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {h.description || "Delivery failed"}
                    {h.error_code ? ` (error ${h.error_code})` : ""}
                    {h.last_failure_at ? ` · last failed ${formatDate(h.last_failure_at)}` : ""}
                  </Text>
                </Box>
              ))}
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter gap={3}>
            <Button ref={cancelRef} onClick={() => answer("cancel")}>
              Cancel
            </Button>
            {batch ? (
              <Button colorScheme="blue" onClick={() => answer("skip")}>
                Skip bad number{single ? "" : "s"} & continue
              </Button>
            ) : (
              <Button colorScheme="red" variant="outline" onClick={() => answer("send")}>
                Send anyway
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
